/**
 * Shared page header (PORTAL-CORRECTIONS-CONTRACT §5, feedback #6).
 *
 * ONE header structure for EVERY page: a title + a one-line subtitle + an
 * optional leading icon + an optional right-aligned action slot. Custom pages
 * (Dashboard, Operations, OKRs, Package Graph, Context Atlas, Event Stream,
 * Docs) and the resource lists all render through this so titles stop looking
 * different from one another.
 *
 * The resource-list `PageHeader` in `resources/projects/_shared.tsx` re-exports
 * this component (mapping its `description`→`subtitle` / `action`→`actions`
 * prop names) so ~35 lists + the ListScaffold keep compiling unchanged.
 */
import * as React from "react";

import { Icon, type IconName } from "@/components/ui/icon";

export interface PageHeaderProps {
  /** The page title (text-2xl). */
  title: string;
  /** One-line description, shown under the title; expected on every page. */
  subtitle?: string;
  /** Optional leading HugeIcon, rendered muted at size 20. */
  icon?: IconName;
  /** Right-aligned action slot (e.g. a "Create" button). */
  actions?: React.ReactNode;
}

export function PageHeader({ title, subtitle, icon, actions }: PageHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-3">
        {icon ? (
          <Icon
            name={icon}
            size={20}
            className="mt-0.5 shrink-0 text-muted-foreground"
            aria-hidden
          />
        ) : null}
        <div className="min-w-0">
          {/* Slim per-page header band (text-xl, tight subtitle) so this second
              header layer stays thin and the page content below dominates. */}
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {subtitle ? (
            <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  );
}

export default PageHeader;
